import { defineComponent as _defineComponent } from 'vue'
import { computed, inject, ref } from 'vue';

import { Offer } from '@/@types/graphql-types';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { UsePromotion } from '@/helpers/composables/usePromotion';
import { usePromotion } from '@/helpers/composables/usePromotion';
import { ClickoutUrl, trackClickout } from '@/helpers/clickout-helper';
import { useAppStore, useLanguageStore } from '@/helpers/composables/useStores';
import { OfferLocation } from '@/helpers/offer-helper';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { FALLBACK_PROMOTION_OFFER, primeImpactCountries } from '@/helpers/promotion-helper';
import { observableOptions, useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { useQuery } from '@/helpers/composables/useApollo';
import {
	GetPackageProviderPlansDocument,
	GetPackageProviderPlansQuery,
} from '@/components/buybox-new/graphql/GetPackageProviderPlans.query';
import { TitleOfferFragment } from '@/components/buybox/graphql/fragments/Offer.fragment';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import PromotionRowOfferDescription from '@/components/buybox-new/buyboxoffer/PromotionRowOfferPresentation.vue';
import PromotionRowOfferLabel from '@/components/buybox-new/buyboxoffer/PromotionRowOfferLabel.vue';
import PromotionRowCTA from '@/components/buybox-new/buyboxoffer/PromotionRowCTA.vue';
import JwTVModal from '@/ui-components/JwTVModal.vue';

import RemoveAds from '@/components/buybox-new/RemoveAds.vue';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';

import { FTSwiperExpVariants } from '../experiments/FreeTrialSwiperExp';
import { BuyBoxFilterOptions } from '@/stores/user.store';
import { getClickoutTrackingPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { OFFER_ENTITYID_KEY } from '@/helpers/composables/useProvideClickoutPlacement';

import GeoSwiperExpName, {
	GeoSwiperExpVariants,
	getSwiperExpOffersKey,
	getSwiperExpTag,
} from '@/components/experiments/GeoSwiperExp';
// GOLDEN_EXP
import ClickoutAnchor from '@/components/nav/links/ClickoutAnchor.vue';
import { ModalHelper } from '@/helpers/modal-helper';

import { useInternationalOffers } from '../experiments/InternationalOffers/composables/useInternationalOffers';

import { useAppleAmzChannelExp } from '../experiments/AppleAmzChannelExp';
import { removeAdsCountries } from '@/components/buybox-new/utils/removeAds';

export type PlanOffer = ReturnType<typeof getPlanOffer>;

import {
	getFewerPromotedTagsExpTagInformation,
	useFewerPromotedTagsExpVariant,
} from '@/components/experiments/FewerPromotedTagsExp';

export default /*#__PURE__*/_defineComponent({
  __name: 'PromotionRowNew',
  props: {
    title: null,
    offers: { default: () => [] },
    additionalContexts: { default: () => [] },
    hardcodedFallbackOfferId: null,
    ftSwiperExpVariant: null,
    geoLocationCountry: null,
    geoSwiperExpVariant: null,
    episodeNumber: null
  },
  emits: ["clickOut", "impressionCallback"],
  setup(__props: any, { emit }: { emit: ({
	(
		e: 'clickOut',
		payload: {
			buyboxOffer: UsePromotion['promotionOffer'];
			offerLocation: OfferLocation.FREE_TRIAL;
			row: HTMLElement | null;
		}
	): void;
	(e: 'impressionCallback'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { title: TitleDetail, offers: Offer[], additionalContexts: SnowplowContext[], hardcodedFallbackOfferId?: number, ftSwiperExpVariant?: FTSwiperExpVariants, geoLocationCountry?: string | null, geoSwiperExpVariant?: GeoSwiperExpVariants | null, episodeNumber?: number | null };





const container = ref<HTMLElement | null>(null);
const titleContext = inject('titleContext', ref<SnowplowTitleContextGraphql | null>(null));
const filterOption = inject('filterOption', ref<BuyBoxFilterOptions | undefined>(undefined));
const titleSourceId = inject<string | null>('titleSourceId', null);
const episodeEntityId = inject<string | undefined>(OFFER_ENTITYID_KEY, undefined);
const { placement } = getClickoutTrackingPlacement();

const { country, language } = useLanguageStore();
const { xServeTrack } = useAppStore();

// APPLE_AMZ_EXP
const { appleAmzChannelExpVariant, setAffiliateParamsAppleAmzExp } = useAppleAmzChannelExp(() => props.title);
// APPLE_AMZ_EXP

const { trackTagEvent, withArbitrageTracking, isArbitrating } = useArbitrage();

// APPLE_AMZ_EXP
const harcodedFallbackId = computed(() => {
	if (appleAmzChannelExpVariant.value === 'variant_3') {
		return 2243;
	}
	return props.hardcodedFallbackOfferId;
});
// APPLE_AMZ_EXP

const offersForPromotion = computed(() => {
	if (!props.geoSwiperExpVariant || !props.geoLocationCountry) {
		return props.offers;
	}

	if (['CA', 'GB', 'DE'].includes(props.geoLocationCountry))
		return props.title[getSwiperExpOffersKey(props.geoLocationCountry)];
	return props.offers;
});

const countryOrGeoCountry = computed(() => {
	return props.geoLocationCountry && props.geoSwiperExpVariant ? props.geoLocationCountry : country.value;
});

const { inPromotionCatalogue, promotionOffer, catalogueItem } = usePromotion({
	title: () => props.title,
	offers: offersForPromotion,
	// APPLE_AMZ_EXP
	hardcodedFallbackOfferId: harcodedFallbackId,
	// APPLE_AMZ_EXP
	countryOrGeoCountry,
});

const packageIds = computed(() =>
	props.geoSwiperExpVariant === 'variant_2' && props.geoLocationCountry === 'CA'
		? [119]
		: [catalogueItem.value?.packageId ?? null].filter(Boolean)
);

const { result: packageProviderPlans } = useQuery<GetPackageProviderPlansQuery>(
	GetPackageProviderPlansDocument,
	() => ({
		country: countryOrGeoCountry.value,
		language: language.value,
		packageIds: packageIds.value,
	}),
	{
		enabled:
			(catalogueItem.value?.placement && !!catalogueItem.value) ||
			(!catalogueItem.value?.placement && !inPromotionCatalogue.value) ||
			(!!props.geoSwiperExpVariant && ['CA', 'GB', 'DE'].includes(props.geoLocationCountry || '')),
		errorPolicy: 'all',
	}
);

// FEWER_PROMOTED_TAGS_EXP
const { variant: fewerPromoExpVariant } = useFewerPromotedTagsExpVariant(props.title.objectType, props.title.offers);
const isHighlightedBorderFromFewerPromoExp = computed(() => {
	if (fewerPromoExpVariant.value) {
		switch (fewerPromoExpVariant.value) {
			case 'control':
			case 'variant_1':
			case 'variant_4':
				return true;

			case 'variant_3':
				return false;

			case 'variant_2':
				return props.episodeNumber !== undefined;
		}
	}
	return isHighlighted.value;
});
const isHighlightedFromFewerPromoExp = computed(() => {
	switch (fewerPromoExpVariant.value) {
		case 'variant_3':
		case 'variant_4':
			return false;

		case 'variant_2':
			return props.episodeNumber !== undefined;
	}
	return isHighlighted.value;
});
const hasPromotionTagFromFewerPromoExp = computed(() => {
	if (!fewerPromoExpVariant.value) {
		return isHighlighted.value;
	}
	if (fewerPromoExpVariant.value === 'control') {
		return true;
	}
	return (
		fewerPromoExpVariant.value === 'variant_1' ||
		(fewerPromoExpVariant.value === 'variant_2' && !!props.episodeNumber)
	);
});
// FEWER_PROMOTED_TAGS_EXP

const offerEntityId = computed(() => episodeEntityId ?? props.title.id);

const crunchyRollUrl = computed(() => {
	const url = props.offers.find(offer => offer.package.packageId === 283)?.standardWebURL;
	if (!url) return 'http://crunchyroll.pxf.io/xk92Nv';

	return url.includes('crunchyroll.pxf.io') ? url : `http://crunchyroll.pxf.io/xk92Nv?u=${url}`;
});

const packagePlan = computed(() => {
	if (props.geoSwiperExpVariant && ['CA', 'GB', 'DE'].includes(props.geoLocationCountry || '')) {
		if (packageProviderPlans.value?.packages?.[0]) return getPlanOffer(packageProviderPlans.value?.packages?.[0]);
		const geoCountryPackage =
			props.title[getSwiperExpOffersKey(props.geoLocationCountry)]?.[0]?.package?.localisedPlanOffers?.[0];

		if (geoCountryPackage) return geoCountryPackage;
	}
	const mainPackage = inPromotionCatalogue.value
		? promotionOffer.value?.package
		: packageProviderPlans.value?.packages?.[0];

	if (!mainPackage) return null;

	return getPlanOffer(mainPackage);
});

const rowNumber = computed(() => {
	if (container.value == null) return undefined;

	const parentBuyBox = container.value.closest?.('.buybox-selector') ?? null;
	const buyBoxRows = Array.from(parentBuyBox?.querySelectorAll('.buybox-selector > *') ?? []);
	const index = buyBoxRows.findIndex(row => row.contains(container.value));

	return index === -1 ? undefined : index;
});

/** Applies `uct_country`, `rowNumber`, `offerLocation`. */
function clickoutBase(u: ClickoutUrl) {
	return u
		.set('uct_country', country.value)
		.addContexts(titleContext.value)
		.set('uct_ft', '1')
		.setTitleSourceId(titleSourceId)
		.updateContext('ClickoutContextGraphql', 'rowNumber', rowNumber.value)
		.updateContext('ClickoutContextGraphql', 'filterOption', filterOption.value)
		.setForTracking('offerLocation', OfferLocation.FREE_TRIAL)
		.updateContext('TitleContextGraphql', 'offerEntityId', offerEntityId.value)
		.updateContext('ClickoutContextGraphql', 'placement', placement)
		.updateContext('ClickoutContextGraphql', 'clickoutType', 'free_trial')
		.setCanary(xServeTrack.value);
}

const hasAppleTvPlusOffer = computed(() => props.offers?.some(offer => offer.package.packageId === 350));
const hasCrunchyRollOffer = computed(() => props.offers?.some(offer => offer.package.packageId === 283));
const hasHuluOffer = computed(() => props.offers?.some(offer => offer.package.packageId === 15));

const { getClickoutUrl, expActiveVariant, trackInternationalOffers } = useInternationalOffers();

const showRemoveAdsBtn = computed(() => {
	// FEWER_PROMO_EXP
	if (fewerPromoExpVariant.value) return false;
	// FEWER_PROMO_EXP

	return removeAdsCountries.includes(country.value);
});

const clickoutLink = computed(() => {
	if (!promotionOffer.value) return null;

	const packageId = promotionOffer.value?.package.packageId ?? 350;

	// // temporary bypass of uct for Crunchyroll
	if (packageId === 283) {
		return new ClickoutUrl({ ...promotionOffer.value, standardWebURL: crunchyRollUrl.value }, { isFreeTrial: true })
			.apply(clickoutBase)
			.set('uct_ft', '1');
	}

	if (isArbitrating.value) {
		const arbitrageUrl = withArbitrageTracking(promotionOffer);
		if (arbitrageUrl) {
			return new ClickoutUrl(
				{ ...promotionOffer.value, standardWebURL: arbitrageUrl.toString() },
				{ skipRedirect: true, isFreeTrial: true }
			).apply(clickoutBase);
		}
	}

	const clickoutUrl = new ClickoutUrl(promotionOffer.value, { isFreeTrial: true }).apply(clickoutBase);

	if (promotionOffer.value?.package.shortName === 'jyn') {
		const clickoutUrl = new ClickoutUrl(
			{ ...promotionOffer.value, standardWebURL: 'https://www.joyn.de/live-tv/prosieben' },
			{ isFreeTrial: true }
		)
			.apply(clickoutBase)
			.set('uct_ft', '1')
			.skipUCT2();
		if (!inPromotionCatalogue.value) {
			clickoutUrl.set('uct_ct', 'fallback');
		}
		return clickoutUrl;
	}

	// FEWER_PROMOTED_TAGS_EXP
	if (fewerPromoExpVariant.value && !inPromotionCatalogue.value) {
		// needs to be in experiment AND needs to be NOT in the promotion catalogue
		const {
			variant,
			country: mCountry,
			number,
			promoType,
		} = getFewerPromotedTagsExpTagInformation(fewerPromoExpVariant.value, country.value);

		// Prime
		const packageId = promotionOffer.value?.package.packageId;
		if ([9, 119].includes(packageId)) {
			const isFallback = promotionOffer.value?.id === FALLBACK_PROMOTION_OFFER;
			const clickoutUrl = new ClickoutUrl(promotionOffer.value, {
				isFreeTrial: promoType === 'ft',
				skipYieldOptimization: true,
			})
				.apply(clickoutBase)
				.setOnOffer('tag', `just${mCountry}-show${promoType}exp-${variant}-${number}`);
			clickoutUrl.set('r', clickoutUrl.get('r')!.replace('app.primevideo.com', 'www.primevideo.com'));

			if (isFallback) {
				clickoutUrl.set('uct_ct', 'fallback');
			}

			return clickoutUrl;
		}
	}
	// FEWER_PROMOTED_TAGS_EXP

	// GEO_SWIPER_EXP
	if (props.geoSwiperExpVariant) {
		if ([9, 119].includes(promotionOffer.value?.package.packageId)) {
			const clickoutUrl = new ClickoutUrl(promotionOffer.value, {
				isFreeTrial: true,
				skipYieldOptimization: true,
			})
				.apply(clickoutBase)
				.setOnOffer('tag', getSwiperExpTag('US', props.geoSwiperExpVariant))
				.skipUCT2();

			if (!inPromotionCatalogue.value) {
				clickoutUrl.set('uct_ct', 'fallback');
			}

			if (props.geoSwiperExpVariant !== 'control' && props.geoLocationCountry) {
				clickoutUrl
					.setOnOffer('tag', getSwiperExpTag(props.geoLocationCountry, props.geoSwiperExpVariant))
					.set('uct_country', props.geoLocationCountry);
			}

			return clickoutUrl.set('r', clickoutUrl.get('r')!.replace('app.primevideo.com', 'www.primevideo.com'));
		}
	}
	// GEO_SWIPER_EXP

	// APPLE_AMZ_EXP
	if (appleAmzChannelExpVariant.value) {
		const clickoutUrl = new ClickoutUrl(promotionOffer.value, {
			isFreeTrial: true,
			skipYieldOptimization: true,
		}).apply(clickoutBase);

		if (!inPromotionCatalogue.value) {
			clickoutUrl.set('uct_ct', 'fallback');
		}

		if (['amp', 'prv'].includes(promotionOffer.value.package.shortName))
			clickoutUrl.set('r', clickoutUrl.get('r')!.replace('app.primevideo.com', 'www.primevideo.com'));

		return setAffiliateParamsAppleAmzExp(clickoutUrl, 'ft', promotionOffer.value.package.shortName);
	}
	// APPLE_AMZ_EXP

	if (promotionOffer.value?.package.packageId !== 350 && promotionOffer.value?.id === FALLBACK_PROMOTION_OFFER) {
		if ([9, 119].includes(promotionOffer.value?.package.packageId)) {
			let offer = promotionOffer.value;
			const ctParam = primeImpactCountries.includes(country.value) ? 'bypass' : 'fallback';

			// INTERNATIONAL_OFFERS_EXP
			// NOTE: IN CASE WE ADDED MORE COUNTRIES TO THE EXPERIMENT, WE NEED TO UPDATE getClickoutUrl
			if (expActiveVariant.value && country.value === 'US') {
				return getClickoutUrl(offer, props.title);
			}
			// INTERNATIONAL_OFFERS_EXP

			const amazonPrimeUrl = new ClickoutUrl(offer, { skipYieldOptimization: true, isFreeTrial: true })
				.set('uct_ct', ctParam)
				.apply(clickoutBase);
			return amazonPrimeUrl.set(
				'r',
				amazonPrimeUrl.get('r')!.replace('app.primevideo.com', 'www.primevideo.com')
			);
		}

		return clickoutUrl;
	}

	const conversionTrackingByPackageId: Record<number, Record<'param' | 'value', string | null>> = {
		350: { value: hasAppleTvPlusOffer.value ? 'atds' : 'natds', param: 'ct' },
		283: { value: hasCrunchyRollOffer.value ? 'free_trial' : null, param: 'subId3' },
		15: { value: hasHuluOffer.value ? 'free_trial' : null, param: 'sub1' },
	};

	const partnerTokenByPackageId: Record<number, Record<'param' | 'value', string>> = {
		350: {
			value: 'justwatch_tv',
			param: 'itsct',
		},
	};

	if (partnerTokenByPackageId[packageId]?.value) {
		clickoutUrl.setOnOffer(partnerTokenByPackageId[packageId].param, partnerTokenByPackageId[packageId].value);
	}

	if (!!conversionTrackingByPackageId[packageId] && conversionTrackingByPackageId[packageId].param) {
		clickoutUrl.setOnOffer(
			conversionTrackingByPackageId[packageId].param!,
			conversionTrackingByPackageId[packageId].value
		);
	}

	if ([9, 119].includes(promotionOffer.value?.package.packageId)) {
		clickoutUrl.set('r', clickoutUrl.get('r')!.replace('app.primevideo.com', 'www.primevideo.com'));
	}

	return clickoutUrl;
});

const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => props.additionalContexts,
});

function onVisibilityChanged(isVisible: boolean) {
	if (!isVisible || !clickoutLink.value) return;

	onIntersection(clickoutLink.value, isVisible);
}

const observeVisibilityOptions = computed(() => ({
	...observableOptions,
	callback: (isVisible: boolean) => onVisibilityChanged(isVisible),
}));

// CTA has a border, and different text. Fallback only.
const isHighlighted = computed(
	() =>
		promotionOffer?.value?.id === FALLBACK_PROMOTION_OFFER &&
		(props.title.__typename === 'Show' || props.title.__typename === 'Season')
);

const isModalProvider = computed(() => promotionOffer.value?.package.shortName === 'jwt');

function openModal() {
	if (!isModalProvider.value) return;
	ModalHelper.openModal(
		JwTVModal,
		{ offer: promotionOffer.value, title: props.title },
		{ cssClass: 'trailer-modal' },
		'',
		true,
		[]
	);

	if (clickoutLink.value) trackClickout(clickoutLink.value);
	onClickOut();
}

function getPlanOffer(pckg: TitleOfferFragment['package'] | GetPackageProviderPlansQuery['packages'][0]) {
	if (pckg.planOffers.length > 0) {
		const plan = pckg.planOffers.find(offer => offer.isTrial || offer.children.length > 0);
		if (plan && plan.children.length > 0) {
			return { ...plan.children[0], retailPrice: plan.retailPrice };
		}
		return plan;
	}
	return null;
}

function onClickOut() {
	trackTagEvent(promotionOffer);

	if (props.geoSwiperExpVariant) {
		const label = props.geoLocationCountry ? `free_trial_${props.geoLocationCountry}` : 'free_trial_US';
		GeoSwiperExpName.trackEvent({ action: 'click', label, property: 'geo_swiper' });
	}

	trackInternationalOffers({
		action: 'click',
		label: 'ft_clickout',
	});

	emit('clickOut', {
		buyboxOffer: promotionOffer?.value,
		offerLocation: OfferLocation.FREE_TRIAL,
		row: container.value,
	});
}

return { __sfc: true,props, emit, container, titleContext, filterOption, titleSourceId, episodeEntityId, placement, country, language, xServeTrack, appleAmzChannelExpVariant, setAffiliateParamsAppleAmzExp, trackTagEvent, withArbitrageTracking, isArbitrating, harcodedFallbackId, offersForPromotion, countryOrGeoCountry, inPromotionCatalogue, promotionOffer, catalogueItem, packageIds, packageProviderPlans, fewerPromoExpVariant, isHighlightedBorderFromFewerPromoExp, isHighlightedFromFewerPromoExp, hasPromotionTagFromFewerPromoExp, offerEntityId, crunchyRollUrl, packagePlan, rowNumber, clickoutBase, hasAppleTvPlusOffer, hasCrunchyRollOffer, hasHuluOffer, getClickoutUrl, expActiveVariant, trackInternationalOffers, showRemoveAdsBtn, clickoutLink, onIntersection, onVisibilityChanged, observeVisibilityOptions, isHighlighted, isModalProvider, openModal, getPlanOffer, onClickOut, ProviderIcon, PromotionRowOfferDescription, PromotionRowOfferLabel, PromotionRowCTA, RemoveAds, ClickoutAnchor }
}

})